import { useAssetTemplateListingFilterer } from '../hooks/useAssetTemplateListingFilterer';
import { AssetTemplateFilterWrapper } from './AssetTemplateFilterWrapper';
import { AssetTemplateItemWrapper } from './AssetTemplateItemWrapper';
import { BlockStack, ResourceList, Spinner } from '@shopify/polaris';
import { AssetTemplateEmptyState } from './AssetTemplateEmptyState';
import { useAssetTemplateContext } from '~/modules/asset-template';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { GPickResourceScrollable } from '~/presentation/common';
import { useAssetTemplatePickerContext } from '../contexts';
import { GBlockCenter } from '~/presentation/ui';
import { dismissOpenTooltips } from '../utils';

// Render templates incrementally so the modal opens instantly instead of mounting
// every (heavy) ResourceItem up-front, then load more batches as the user scrolls.
const INITIAL_VISIBLE_COUNT = 50;
const LOAD_MORE_BATCH_SIZE = 50;
// Start loading the next batch before the user reaches the very bottom.
const LOAD_MORE_OFFSET_PX = 300;

// Device capability is static, so compute it once instead of on every scroll event.
const isTouchDevice =
  typeof window !== 'undefined' && window.matchMedia('(hover: none), (pointer: coarse)').matches;

export const AssetTemplateListing: React.FC = () => {
  const { selectedItems, isSelectedDisplayAll } = useAssetTemplatePickerContext();
  const { handleSetSelectedItems } = useAssetTemplatePickerContext();
  const { isLoadingAsset, modalAssetTemplate } = useAssetTemplateContext();
  const { templateListing, isFetchingUnpublishedThemePages } = useAssetTemplateListingFilterer();
  const { isOpenModalMultiple } = modalAssetTemplate;

  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_COUNT);
  const totalCount = templateListing?.length || 0;

  // Reset the window whenever the filtered/searched result set changes.
  useEffect(() => {
    setVisibleCount(INITIAL_VISIBLE_COUNT);
  }, [templateListing]);

  const visibleItems = useMemo(
    () => (templateListing || []).slice(0, visibleCount),
    [templateListing, visibleCount],
  );
  const hasMoreItems = visibleCount < totalCount;

  const handleLoadMore = useCallback(() => {
    setVisibleCount((prev) => (prev >= totalCount ? prev : prev + LOAD_MORE_BATCH_SIZE));
  }, [totalCount]);

  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      // Touch-only: on desktop the pointer stays over the item after scrolling, so closing would
      // leave the tooltip hidden until the cursor moves — and desktop closes it naturally anyway.
      if (isTouchDevice) {
        dismissOpenTooltips();
      }

      const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
      if (scrollTop + clientHeight >= scrollHeight - LOAD_MORE_OFFSET_PX) {
        handleLoadMore();
      }
    },
    [handleLoadMore],
  );

  return (
    <BlockStack>
      <AssetTemplateFilterWrapper />
      <GPickResourceScrollable
        variant="default"
        horizontal
        onScroll={handleScroll}
        className="Polaris-GTemplate"
      >
        <ResourceList
          resourceName={{ singular: 'template', plural: 'templates' }}
          items={visibleItems}
          totalItemsCount={totalCount}
          hasMoreItems={hasMoreItems}
          emptyState={
            <GBlockCenter minHeight="calc(80vh - 194px)">
              <AssetTemplateEmptyState />
            </GBlockCenter>
          }
          loading={isLoadingAsset || isFetchingUnpublishedThemePages}
          showHeader={!isSelectedDisplayAll}
          onSelectionChange={handleSetSelectedItems}
          selectedItems={selectedItems}
          {...(isOpenModalMultiple && {
            selectable: true,
          })}
          flushFilters
          renderItem={(item, key) => {
            return <AssetTemplateItemWrapper key={key} template={item} />;
          }}
        />
        {hasMoreItems && (
          <div className="flex justify-center py-3">
            <Spinner accessibilityLabel="Loading more templates" size="small" />
          </div>
        )}
        {isLoadingAsset && (
          <div className="Polaris-GTemplate--loading h-full">
            <Spinner accessibilityLabel="Small spinner example" size="small" />
          </div>
        )}
      </GPickResourceScrollable>
    </BlockStack>
  );
};
